// WsTransport: the session socket as the server serves it — one
// protobuf-binary Down per WebSocket message on GET /session, the token
// as the second WebSocket subprotocol beside "psypen", the client's ver
// and os in the query, and since=<seq> when resuming. Frames Up are
// protobuf-binary too. Ups sent before the socket opens are buffered and
// flushed on open; the server holds them until its snapshot is out.
import { fromBinary, toBinary } from "@bufbuild/protobuf";
import { DownSchema, UpSchema } from "@/gen/psypen/v1/session_pb";
import type { Down, Up } from "@/gen/psypen/v1/session_pb";
import type { Transport, TransportHandlers } from "@/session/transport";

export interface WsTransportOptions {
  // The server's origin, any scheme: "https://dev.api.psypen.app" or
  // "http://localhost:8080" — it becomes ws:// or wss:// here.
  origin: string;
  // The bearer token; empty for a server that runs anonymous in local
  // development.
  token?: string;
  // ver and os name the client in analytics, as the orb sends them.
  ver: string;
  os: string;
}

function wsOrigin(origin: string): string {
  const url = new URL(origin);
  if (url.protocol === "https:") url.protocol = "wss:";
  else if (url.protocol === "http:") url.protocol = "ws:";
  else throw new Error(`ws transport: not an http origin: ${origin}`);
  return url.origin;
}

export class WsTransport implements Transport {
  private ws: WebSocket | null = null;
  private handlers: (TransportHandlers & { since?: bigint }) | null = null;
  private buffered: Uint8Array[] = [];
  private opened = false;
  private userClosed = false;

  constructor(private readonly opts: WsTransportOptions) {}

  connect(h: TransportHandlers & { since?: bigint }): void {
    if (this.ws) throw new Error("ws transport: connect called twice");
    this.handlers = h;
    const url = new URL("/session", wsOrigin(this.opts.origin));
    url.searchParams.set("ver", this.opts.ver);
    url.searchParams.set("os", this.opts.os);
    if (h.since !== undefined) url.searchParams.set("since", h.since.toString());
    // The token rides the subprotocol, which a page of another origin
    // cannot hold; the server answers "psypen".
    const protocols = this.opts.token ? ["psypen", this.opts.token] : ["psypen"];
    const ws = new WebSocket(url, protocols);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    ws.onopen = () => {
      this.opened = true;
      const out = this.buffered;
      this.buffered = [];
      for (const b of out) ws.send(b);
      this.handlers?.onOpen();
    };
    ws.onmessage = (ev) => {
      const data = ev.data;
      if (!(data instanceof ArrayBuffer)) {
        this.fail(new Error("ws transport: a non-binary frame"));
        return;
      }
      let down: Down;
      try {
        down = fromBinary(DownSchema, new Uint8Array(data));
      } catch (err) {
        this.fail(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      this.handlers?.onDown(down);
    };
    ws.onclose = () => {
      // The browser's close event carries no reason; a socket that never
      // opened reports that, a user close reports nothing at all.
      const h = this.handlers;
      this.handlers = null;
      h?.onClose(this.userClosed || this.opened ? undefined : new Error("ws transport: the socket never opened"));
    };
    ws.onerror = () => {
      // onclose follows onerror in every browser; nothing to do here.
    };
  }

  send(u: Up): void {
    const ws = this.ws;
    if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
      throw new Error("ws transport: the socket is closed");
    }
    const bytes = toBinary(UpSchema, u);
    // Buffer until our own open handler ran: a browser may report OPEN
    // before onopen fires, and a direct send then would jump the queue
    // of Ups buffered while connecting (order matters: audio_format
    // before audio).
    if (!this.opened) {
      this.buffered.push(bytes);
      return;
    }
    ws.send(bytes);
  }

  close(): void {
    this.userClosed = true;
    this.ws?.close();
  }

  private fail(err: Error): void {
    this.ws?.close();
    // onclose delivers the error only as "it closed"; name it here so a
    // protocol failure is not silent. The handler is dropped first so
    // onClose runs once, through this call.
    const h = this.handlers;
    this.handlers = null;
    h?.onClose(err);
    // onclose will still fire; with no handlers it is a no-op.
  }
}
